import React, { useEffect, useState } from "react";
import "./OpChargesReport.css";
import ENDPOINTS from "../../../../Constants/ApiEndpoints";
import axios from "axios";
import {
  FaWallet,
  FaReceipt,
  FaStar,
  FaSearch,
  FaCalendarAlt,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaFileInvoiceDollar
} from "react-icons/fa";
import DateRangePickerComponent from "../../../../CommonComponents/DateRangePickerComponent";
import CategoryRecordsModal from "../Payments/CategoryRecordsModal";

const OpChargesReport = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Modal drilldown state
  const [selectedCategory, setSelectedCategory] = useState(null);

  const formatDateToIso = (dateObj) => {
    if (!dateObj) return "";
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchOpChargesReport = async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const fromStr = formatDateToIso(start);
      const toStr = formatDateToIso(end);

      const response = await axios.get(`${ENDPOINTS.GET_OP_CHARGES_REPORT}`, {
        params: {
          fromDate: fromStr,
          toDate: toStr,
        },
      });

      setReportData(response.data);
    } catch (err) {
      console.error("Failed to fetch OP charges report:", err);
      setError("Unable to load outpatient charges data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpChargesReport(startDate, endDate);
  }, [startDate, endDate]);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  const chargesList = reportData?.chargesList || [];
  const filteredCharges = chargesList.filter((item) => {
    if (!searchQuery.trim()) return true;
    return item.charge && item.charge.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const totalCount = filteredCharges.reduce((sum, item) => sum + (item.count || 0), 0);
  const totalAmount = filteredCharges.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalPaid = filteredCharges.reduce((sum, item) => sum + (item.amountPaid || 0), 0);

  return (
    <div className="op-charges-master-wrapper">
      <div className="unified-master-card">
        <div className="master-card-accent-bar" />

        {/* Header Toolbar */}
        <div className="master-toolbar-header">
          <div className="toolbar-left-group">
            <div className="toolbar-date-picker">
              <DateRangePickerComponent
                startDate={startDate}
                endDate={endDate}
                onDateChange={handleDateChange}
              />
            </div>
            <div className="report-search-box">
              <FaSearch size={13} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search charge category (e.g. Consultation, Lab)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <button
            className="refresh-report-btn"
            onClick={() => fetchOpChargesReport(startDate, endDate)}
          >
            Refresh Report
          </button>
        </div>

        {/* Top Summary KPI Cards */}
        <div className="report-kpi-grid">
          <div className="kpi-card total-kpi">
            <div className="kpi-icon-wrapper blue-icon">
              <FaWallet size={18} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">TOTAL OP CHARGES</span>
              <h3 className="kpi-value">
                ₹ {(reportData?.totalOpChargesAmount || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </h3>
              <div className="kpi-sub-strip">
                <span className="kpi-sub-paid">
                  Net Paid: ₹ {(reportData?.totalOpChargesPaidAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </span>
                {(reportData?.totalOpChargesRefundAmount || 0) > 0 && (
                  <span className="kpi-sub-refund">
                    (₹ {(reportData.totalOpChargesRefundAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })} refunded)
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="kpi-card count-kpi">
            <div className="kpi-icon-wrapper green-icon">
              <FaReceipt size={18} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">CHARGES COUNT</span>
              <h3 className="kpi-value">{reportData?.totalChargesCount || 0} Entries</h3>
            </div>
          </div>

          <div className="kpi-card top-kpi">
            <div className="kpi-icon-wrapper purple-icon">
              <FaStar size={18} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">TOP CHARGE CATEGORY</span>
              <h3 className="kpi-value top-cat-text">
                {reportData?.topCategory || "N/A"}
              </h3>
            </div>
          </div>
        </div>

        {/* Loading / Error States */}
        {loading && (
          <div className="report-loading-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading outpatient charges data...</p>
          </div>
        )}

        {error && !loading && (
          <div className="report-error-state">
            <FaInfoCircle size={20} color="#ef4444" />
            <p>{error}</p>
          </div>
        )}

        {/* Charges Breakdown Table */}
        {!loading && !error && (
          <div className="transactions-section">
            <div className="transactions-section-header">
              <div className="header-title-group">
                <FaFileInvoiceDollar size={16} color="#2563eb" />
                <h3 className="table-title">Outpatient Charges Breakdown</h3>
              </div>
              <span className="count-pill-badge">{filteredCharges.length} Categories</span>
            </div>

            <div className="table-responsive">
              <table className="report-data-table">
                <thead>
                  <tr>
                    <th>
                      <div className="th-flex">
                        <FaFileInvoiceDollar size={11} color="#64748b" />
                        <span>CHARGE CATEGORY</span>
                      </div>
                    </th>
                    <th className="text-center" style={{ width: "160px" }}>
                      <div className="th-flex center-justify">
                        <FaReceipt size={11} color="#64748b" />
                        <span>ENTRY COUNT</span>
                      </div>
                    </th>
                    <th className="text-right" style={{ width: "220px" }}>
                      <div className="th-flex right-justify">
                        <FaWallet size={11} color="#64748b" />
                        <span>TOTAL AMOUNT (₹)</span>
                      </div>
                    </th>
                    <th className="text-right" style={{ width: "240px" }}>
                      <div className="th-flex right-justify">
                        <FaWallet size={11} color="#16a34a" />
                        <span>PAID AMOUNT (₹)</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCharges.length > 0 ? (
                    filteredCharges.map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleCategoryClick(item.charge)}
                        title="Click to view detailed transaction records"
                      >
                        <td>
                          <div className="charge-category-cell">
                            <span className="charge-avatar-circle">
                              <FaFileInvoiceDollar size={12} color="#2563eb" />
                            </span>
                            <strong className="charge-title">{item.charge}</strong>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className="count-badge">{item.count}</span>
                        </td>
                        <td className="text-right">
                          <span className="amount-val">
                            ₹ {(item.amount || 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </td>
                        <td className="text-right">
                          <div className="amount-cell-flex-right">
                            <span className="amount-val paid-val">
                              ₹ {(item.amountPaid || 0).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                            {item.refundAmount > 0 && (
                              <span className="refund-subtext-tag">
                                (₹ {item.refundAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })} refunded)
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="no-data-cell">
                        No outpatient charges recorded for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredCharges.length > 0 && (
                  <tfoot>
                    <tr className="total-summary-row">
                      <td>
                        <strong>TOTAL OUTPATIENT CHARGES</strong>
                      </td>
                      <td className="text-center">
                        <strong className="total-count-badge">{totalCount} Entries</strong>
                      </td>
                      <td className="text-right">
                        <strong className="total-amount-text">
                          ₹ {totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </strong>
                      </td>
                      <td className="text-right">
                        <div className="amount-cell-flex-right">
                          <strong className="total-paid-text">
                            ₹ {totalPaid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </strong>
                          {reportData?.totalOpChargesRefundAmount > 0 && (
                            <span className="refund-subtext-tag">
                              (₹ {reportData.totalOpChargesRefundAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })} refunded)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Drilldown Category Details Modal */}
      {selectedCategory && (
        <CategoryRecordsModal
          isOpen={!!selectedCategory}
          categoryData={{ invoiceName: selectedCategory }}
          fromDate={formatDateToIso(startDate)}
          toDate={formatDateToIso(endDate)}
          showDoctorAndAddedBy={false}
          onClose={() => setSelectedCategory(null)}
        />
      )}
    </div>
  );
};

export default OpChargesReport;
